import { useNavigate } from "react-router-dom";
import MobileTailwind from "./MobileTailwind";
import { CartLine, useCart } from "./CartContext";
import CartLineItem from "./components/CartLineItem";
import BillingDetails from "./components/BillingDetails";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import WebService from "../Services/WebService";
import { getEncryptedSessionData, setEncryptedSessionData } from "../Services/SessionStorageService";
import { getPosOrderCacheKey } from "./Tables";
import { io } from "socket.io-client";
import SubHeader from "./components/SubHeader";
import VegIcon from "./components/VegIcon";

const getDishImage = (name: string): string => {
  const n = name.toLowerCase();
  if (n.includes("paneer")) return "https://images.unsplash.com/photo-1631452180519-c014fe946bc7?w=150&auto=format&fit=crop&q=60";
  if (n.includes("soup")) return "https://images.unsplash.com/photo-1547592180-85f173990554?w=150&auto=format&fit=crop&q=60";
  if (n.includes("biryani") || n.includes("rice")) return "https://images.unsplash.com/photo-1633945274405-b6c8069047b0?w=150&auto=format&fit=crop&q=60";
  if (n.includes("chicken")) return "https://images.unsplash.com/photo-1604908176997-125f25cc6f3d?w=150&auto=format&fit=crop&q=60";
  if (n.includes("bread") || n.includes("roti") || n.includes("naan")) return "https://images.unsplash.com/photo-1589301760014-d929f3979dbc?w=150&auto=format&fit=crop&q=60";
  if (n.includes("noodle") || n.includes("noodles")) return "https://images.unsplash.com/photo-1585032226651-759b368d7246?w=150&auto=format&fit=crop&q=60";
  if (n.includes("starter") || n.includes("sixty five") || n.includes("65") || n.includes("kebab") || n.includes("tikka")) return "https://images.unsplash.com/photo-1565557623262-b51c2513a641?w=150&auto=format&fit=crop&q=60";
  return "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=150&auto=format&fit=crop&q=60";
};

const getPosSocket = () => {
  const envUrl = process.env.REACT_APP_POS_SOCKET_URL;
  const stored = localStorage.getItem("pos_socket_url");
  const host = window.location.hostname;
  let url = envUrl || stored || "http://localhost:3007";
  if (!envUrl && (!stored || stored === "http://localhost:3007") && host !== "localhost" && host !== "127.0.0.1") {
    url = `http://${host}:3007`;
  }
  return io(url, { autoConnect: true, transports: ["websocket", "polling"] });
};
let _posSocket: ReturnType<typeof io> | null = null;
const posSocket = () => {
  if (!_posSocket) _posSocket = getPosSocket();
  return _posSocket;
};

const sortTableNumbers = (tables: string[]) =>
  [...tables].sort((a, b) => {
    const numA = parseInt(a.replace(/\D/g, ""), 10);
    const numB = parseInt(b.replace(/\D/g, ""), 10);
    if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
    return a.localeCompare(b);
  });

const getMenuItemCode = (item: any, codeById: Record<string, string | number> = {}) => {
  const id = String(item.menu_id || item.id || item.item_id || "");
  return item.custom_code ?? item.code ?? item.item_code ?? item.dish_code ?? item.menu_code ?? codeById[id] ?? 0;
};

const getDishCartPrice = (dish: any) => {
  const price = Number(dish?.price || 0);
  const grossPrice = Number(dish?.gross_price ?? price);
  return grossPrice > 0 && grossPrice < price ? grossPrice : price;
};

const getMenuMasterPrice = (item: any) => {
  const values = [item.price, item.gross_price, item.unit_price]
    .map((value) => Number(value))
    .filter((value) => value > 0);
  return values.length > 0 ? Math.min(...values) : 0;
};

const mapMenuItemToCartLine = (
  item: any,
  idx: number,
  codeById: Record<string, string | number> = {},
  menuPriceById: Record<string, number> = {}
) => {
  const dishId = String(item.menu_id || item.id || item.item_id || `item_${idx}`);
  const dishCode = getMenuItemCode(item, codeById);
  const price = menuPriceById[dishId] ?? Number(item.price ?? item.gross_price ?? item.unit_price ?? 0);
  const dish = {
    id: dishId,
    name: item.name || item.item_name || item.menu_name || "Item",
    code: dishCode,
    price,
    gross_price: price,
    type: (item.type || (String(item.food_type || "").toLowerCase().includes("non") ? "nonveg" : "veg")) as "veg" | "nonveg",
  };

  return {
    key: `${dish.id}-${dish.code}`,
    dish,
    quantity: Number(item.quantity || item.qty || 1),
    note: item.special_note || item.note || "",
  };
};

const getSavedWaiterCarts = (cacheKey: string) => {
  try {
    const saved = localStorage.getItem(cacheKey);
    return saved ? JSON.parse(saved) : {};
  } catch {
    return {};
  }
};

const getOrderPriceById = (menus: any[]) => {
  const priceById: Record<string, number> = {};
  menus.forEach((item: any) => {
    const id = String(item.menu_id || item.id || item.item_id || "");
    const price = Number(item.price ?? item.gross_price ?? item.unit_price ?? 0);
    if (id && price > 0) priceById[id] = price;
  });
  return priceById;
};

const mergeCartLines = (baseLines: CartLine[], movedLines: CartLine[]) => {
  const merged = [...baseLines];
  movedLines.forEach((line) => {
    const existingIndex = merged.findIndex((item) => item.key === line.key);
    if (existingIndex > -1) {
      merged[existingIndex] = {
        ...merged[existingIndex],
        quantity: Number(merged[existingIndex].quantity || 0) + Number(line.quantity || 0),
      };
    } else {
      merged.push(line);
    }
  });
  return merged;
};

const enrichCartLinesWithCodes = (
  lines: any[],
  codeById: Record<string, string | number>,
  orderPriceById: Record<string, number> = {},
  menuPriceById: Record<string, number> = {}
) =>
  lines.map((line) => {
    const dishId = String(line?.dish?.id || "");
    const currentCode = line?.dish?.code;
    const resolvedCode = currentCode && currentCode !== 0 && currentCode !== "0" ? currentCode : codeById[dishId] ?? currentCode ?? 0;
    const resolvedPrice = menuPriceById[dishId] ?? orderPriceById[dishId] ?? getDishCartPrice(line?.dish);
    return {
      ...line,
      key: `${dishId}-${resolvedCode}`,
      dish: {
        ...line.dish,
        code: resolvedCode,
        price: resolvedPrice,
        gross_price: resolvedPrice,
      },
    };
  });

const Cart = () => {
  const navigate = useNavigate();
  const cart = useCart();
  const [loading, setLoading] = useState(false);
  const tenantId = localStorage.getItem("tenantId") || "default";
  const isEmpty = cart.lines.length === 0;
  const [kitchenNote, setKitchenNote] = useState("");
  const [tableDropdownOpen, setTableDropdownOpen] = useState(false);
  const [tableSearch, setTableSearch] = useState("");
  const [moveMode, setMoveMode] = useState(false);
  const [selectedMoveKeys, setSelectedMoveKeys] = useState<string[]>([]);
  const [moveTableDropdownOpen, setMoveTableDropdownOpen] = useState(false);
  const [moveTableSearch, setMoveTableSearch] = useState("");
  const [moveTargetTable, setMoveTargetTable] = useState("");
  const [allTables, setAllTables] = useState<string[]>([]);
  const [menuCodeById, setMenuCodeById] = useState<Record<string, string | number>>({});
  const [menuPriceById, setMenuPriceById] = useState<Record<string, number>>({});
  const posOrderCacheKey = getPosOrderCacheKey(tenantId);
  const waiterCartCacheKey = `waiter_carts_${tenantId}`;
  const [posOrdersByTable, setPosOrdersByTable] = useState<Record<string, any>>(
    () => getEncryptedSessionData<Record<string, any>>(posOrderCacheKey) || {}
  );
  const activeTableNo = cart.tableNo;
  const cartLines = cart.lines;
  const setCartLines = cart.setCartLines;

  useEffect(() => {
    WebService.getAPI({ action: "/get-all-lounge-list", body: {} })
      .then((response: any) => {
        const lounges: any[] = response?.lounges || [];
        const tablesSet = new Set<string>();
        lounges.forEach((lounge) => {
          let tablesList: any[] = [];
          if (Array.isArray(lounge.lounge_tables)) {
            tablesList = lounge.lounge_tables;
          } else {
            try {
              tablesList = JSON.parse(lounge.lounge_tables);
            } catch {
              tablesList = [];
            }
          }
          if (Array.isArray(tablesList)) {
            tablesList.forEach((t) => {
              const no = typeof t === "object" && t !== null ? t.table_no || t.tableNo || t.id : String(t);
              if (no) tablesSet.add(String(no).trim());
            });
          }
        });
        Object.keys(posOrdersByTable).forEach((tableNo) => tablesSet.add(String(tableNo).trim()));
        setAllTables(sortTableNumbers(Array.from(tablesSet).filter(Boolean)));
      })
      .catch((err) => {
        console.error("Error fetching lounge tables:", err);
        setAllTables(sortTableNumbers(Object.keys(posOrdersByTable)));
      });
  }, [posOrdersByTable]);

  useEffect(() => {
    Promise.all([
      WebService.getAPI({ action: "/get-all/menu-items", body: {} }),
      WebService.getAPI({ action: "/get-all/special-menu-items", body: {} }),
    ])
      .then(([resNormal, resSpecial]: any[]) => {
        const rawItems = [
          ...(resNormal?.menus || resNormal?.items || []),
          ...(resSpecial?.menus || []),
        ];
        const nextCodeById: Record<string, string | number> = {};
        const nextPriceById: Record<string, number> = {};
        rawItems.forEach((item: any) => {
          const id = String(item.id || item.menu_id || item.item_id || "");
          const code = item.custom_code ?? item.code ?? item.item_code ?? item.dish_code ?? item.menu_code;
          if (id && code !== undefined && code !== null && code !== "") {
            nextCodeById[id] = code;
          }
          const price = getMenuMasterPrice(item);
          if (id && price > 0) {
            nextPriceById[id] = price;
          }
        });
        setMenuCodeById(nextCodeById);
        setMenuPriceById(nextPriceById);
      })
      .catch((err) => console.error("Menu code fetch error:", err));
  }, []);

  useEffect(() => {
    if (!activeTableNo || (Object.keys(menuCodeById).length === 0 && Object.keys(menuPriceById).length === 0)) return;
    const orderPriceById = getOrderPriceById(posOrdersByTable[activeTableNo]?.menus || []);
    const enrichedLines = enrichCartLinesWithCodes(cartLines, menuCodeById, orderPriceById, menuPriceById);
    const hasChanges = enrichedLines.some((line, idx) => (
      line.dish.code !== cartLines[idx]?.dish?.code ||
      line.dish.price !== cartLines[idx]?.dish?.price ||
      line.dish.gross_price !== cartLines[idx]?.dish?.gross_price
    ));
    if (!hasChanges) return;
    const hasResolvedCodes = enrichedLines.some((line, idx) => {
      const oldCode = cartLines[idx]?.dish?.code;
      return line.dish.code !== oldCode && line.dish.code !== 0 && line.dish.code !== "0";
    });
    const hasResolvedPrices = enrichedLines.some((line, idx) => line.dish.price !== cartLines[idx]?.dish?.price);
    if (hasResolvedCodes || hasResolvedPrices) setCartLines(activeTableNo, enrichedLines);
  }, [activeTableNo, cartLines, setCartLines, menuCodeById, menuPriceById, posOrdersByTable]);

  const availableTables = useMemo(() => {
    const tables = allTables.length > 0 ? allTables : Object.keys(posOrdersByTable);
    return sortTableNumbers(tables);
  }, [allTables, posOrdersByTable]);

  const filteredTables = useMemo(() => {
    const q = tableSearch.trim().toLowerCase();
    if (!q) return availableTables;
    return availableTables.filter((tableNo) => tableNo.toLowerCase().includes(q));
  }, [availableTables, tableSearch]);

  const filteredMoveTables = useMemo(() => {
    const q = moveTableSearch.trim().toLowerCase();
    const tables = availableTables.filter((tableNo) => String(tableNo) !== String(cart.tableNo));
    if (!q) return tables;
    return tables.filter((tableNo) => tableNo.toLowerCase().includes(q));
  }, [availableTables, moveTableSearch, cart.tableNo]);

  const handleSelectTable = (tableNo: string) => {
    const savedCarts = getSavedWaiterCarts(waiterCartCacheKey);
    const hasSavedCart = Object.prototype.hasOwnProperty.call(savedCarts, tableNo);
    const savedCartLines = savedCarts[tableNo] || [];
    const tableOrder = posOrdersByTable[tableNo];
    const menus = tableOrder?.menus || [];
    const orderPriceById = getOrderPriceById(menus);
    if (hasSavedCart) {
      cart.setCartLines(tableNo, enrichCartLinesWithCodes(savedCartLines, menuCodeById, orderPriceById, menuPriceById));
    } else {
      cart.setCartLines(tableNo, menus.map((item: any, idx: number) => mapMenuItemToCartLine(item, idx, menuCodeById, menuPriceById)));
    }
    cart.setTable(tableNo);
    setTableDropdownOpen(false);
    setTableSearch("");
    setMoveMode(false);
    setSelectedMoveKeys([]);
  };

  const toggleMoveMode = () => {
    setMoveMode((open) => {
      const next = !open;
      if (!next) setSelectedMoveKeys([]);
      return next;
    });
  };

  const toggleMoveSelection = (key: string) => {
    setSelectedMoveKeys((prev) =>
      prev.includes(key) ? prev.filter((itemKey) => itemKey !== key) : [...prev, key]
    );
  };

  const handleMoveItems = () => {
    if (!cart.tableNo) {
      toast.error("Please select a table");
      return;
    }
    if (!moveMode) {
      toast.error("Please click Move Item and select items");
      return;
    }
    if (selectedMoveKeys.length === 0) {
      toast.error("Please select item to move");
      return;
    }
    if (!moveTargetTable) {
      toast.error("Please select target table");
      return;
    }
    if (String(moveTargetTable) === String(cart.tableNo)) {
      toast.error("Please select another table");
      return;
    }

    const selectedLines = cart.lines.filter((line) => selectedMoveKeys.includes(line.key));
    const remainingLines = cart.lines.filter((line) => !selectedMoveKeys.includes(line.key));
    const savedCarts = getSavedWaiterCarts(waiterCartCacheKey);
    const hasTargetSavedCart = Object.prototype.hasOwnProperty.call(savedCarts, moveTargetTable);
    const targetOrderMenus = posOrdersByTable[moveTargetTable]?.menus || [];
    const targetOrderPriceById = getOrderPriceById(targetOrderMenus);
    const targetExistingLines = hasTargetSavedCart
      ? enrichCartLinesWithCodes(savedCarts[moveTargetTable] || [], menuCodeById, targetOrderPriceById, menuPriceById)
      : targetOrderMenus.map((item: any, idx: number) => mapMenuItemToCartLine(item, idx, menuCodeById, menuPriceById));

    cart.setCartLines(cart.tableNo, remainingLines);
    cart.setCartLines(moveTargetTable, mergeCartLines(targetExistingLines, selectedLines));
    setMoveMode(false);
    setSelectedMoveKeys([]);
    setMoveTableDropdownOpen(false);
    setMoveTableSearch("");
    toast.success(`Items moved to Table ${moveTargetTable}`);
  };

  const handleAddMoreItems = () => {
    if (cart.tableNo) {
      navigate(`/mobile/menu/${cart.tableNo}`);
    } else {
      navigate(-1);
    }
  };

  const handleClearCart = () => {
    if (!cart.tableNo) return;
    const nextPosOrders = { ...posOrdersByTable };
    delete nextPosOrders[cart.tableNo];
    setPosOrdersByTable(nextPosOrders);
    setEncryptedSessionData(posOrderCacheKey, nextPosOrders);

    const qrOrderCacheKey = `qr_table_orders_${tenantId}`;
    const cachedQrOrders = getEncryptedSessionData<Record<string, any>>(qrOrderCacheKey) || {};
    if (cachedQrOrders[cart.tableNo]) {
      delete cachedQrOrders[cart.tableNo];
      setEncryptedSessionData(qrOrderCacheKey, cachedQrOrders);
    }

    cart.clear();
  };

  const handlePlaceOrder = () => {
    if (isEmpty) { toast.error("Please add at least one menu item"); return; }
    if (!cart.tableNo) { toast.error("Please select a table"); return; }
    setLoading(true);

    const userInfoStr = localStorage.getItem("user_info");
    const userInfo = userInfoStr ? JSON.parse(userInfoStr) : null;

    const menusPayload = cart.lines.map((line) => ({
      id: line.dish.id,
      quantity: line.quantity,
      gross_price: getDishCartPrice(line.dish),
      special_note: line.note || "",
    }));

    WebService.postAPI({
      action: `/create/waiter-order`,
      body: {
        table_no: cart.tableNo,
        tenantId: tenantId,
        menus: menusPayload,
        waiter_id: userInfo?.id || "",
      },
      id: "saveUserButton",
    })
      .then((res: any) => {
        setLoading(false);
        toast.success("Order placed successfully!");

        // ── Naye items banao ──
        const newMenus = cart.lines.map((line, idx) => ({
          menu_id: line.dish.id || `item_${idx}`,
          id: line.dish.id || `item_${idx}`,
          name: line.dish.name || "",
          code: line.dish.code || 0,
          custom_code: line.dish.code || 0,
          gross_price: getDishCartPrice(line.dish),
          price: getDishCartPrice(line.dish),
          quantity: Number(line.quantity || 1),
          kot_id: null,
        }));

        const posKey = getPosOrderCacheKey(tenantId);
        const existing = getEncryptedSessionData<Record<string, any>>(posKey) || {};
        const tableCache = existing[cart.tableNo!];

        if (tableCache?.type === "QR_ORDER") {
          // newMenus has both QR items and waiter added items
          existing[cart.tableNo!] = {
            ...(tableCache || {}),
            menus: newMenus,
            timestamp: new Date().toISOString(),
            type: "POS_ORDER",
            loungeId: tableCache?.loungeId || null,
            tokenNumber: res?.token_number || tableCache?.tokenNumber || "",
            isNew: true,
          };
        } else {
          // ── Purane menus ke saath merge karo ──
          const oldMenus: any[] = tableCache?.menus || [];
          const mergedMenus = [...oldMenus];
          newMenus.forEach((newItem) => {
            const existingIndex = mergedMenus.findIndex(
              (m) => String(m.menu_id || m.id) === String(newItem.menu_id)
            );
            if (existingIndex > -1) {
              mergedMenus[existingIndex] = {
                ...mergedMenus[existingIndex],
                quantity: Number(mergedMenus[existingIndex].quantity || 1) + Number(newItem.quantity || 1),
              };
            } else {
              mergedMenus.push(newItem);
            }
          });

          existing[cart.tableNo!] = {
            ...(tableCache || {}),
            menus: mergedMenus,
            timestamp: tableCache?.timestamp || new Date().toISOString(),
            type: "POS_ORDER",
            loungeId: tableCache?.loungeId || null,
            tokenNumber: res?.token_number || tableCache?.tokenNumber || "",
            isNew: true,
          };
        }
        setEncryptedSessionData(posKey, existing);

        // ── Clear QR order from cache if it exists ──
        const qrOrderCacheKey = `qr_table_orders_${tenantId}`;
        const cachedQrOrders = getEncryptedSessionData<Record<string, any>>(qrOrderCacheKey) || {};
        if (cachedQrOrders[cart.tableNo!]) {
          delete cachedQrOrders[cart.tableNo!];
          setEncryptedSessionData(qrOrderCacheKey, cachedQrOrders);
        }

        // ── POS socket ko notify karo ──
        const s = posSocket();
        const userInfoStr = localStorage.getItem("user_info");
        const userInfo = userInfoStr ? JSON.parse(userInfoStr) : null;
        const emitPayload = {
          table_no: cart.tableNo,
          lounge_id: tableCache?.loungeId || 1,
          token_number: res?.token_number || "",
          action: "add_menu_items_on_table",
          menus: menusPayload,
          waiter_id: userInfo?.id || "",
          captain_id: "",
          persons: 1,
          special_note: "",
        };
        if (s.connected) {
          s.emit("waiterOrderCreated", emitPayload);
        } else {
          s.once("connect", () => s.emit("waiterOrderCreated", emitPayload));
          s.connect();
        }

        cart.clear();
        navigate("/mobile/order-success");
      })
      .catch((error: any) => {
        setLoading(false);
        toast.error(error?.message || "Failed to place order");
      });
  };

  return (
    <>
      <MobileTailwind />
      <div id="mobile-root" className="min-h-screen w-full bg-black flex justify-center">
        <div className="relative w-full min-h-screen overflow-x-hidden" style={{ backgroundColor: "#F4F4F5" }}>
          {/* Header */}
          <SubHeader title="Cart" />

          {/* Mobile view container (hidden on md screens and above) */}
          <div className="block md:hidden pb-[120px]">
            {/* Table number */}
            <div className="px-3 pt-4 pb-3 flex items-center justify-between gap-3">
              {cart.tableNo && (
                <h2 className="font-sansation font-bold text-[#111] text-[16px]">
                  Table Number - {cart.tableNo}
                </h2>
              )}
              {availableTables.length > 0 && (
                <div className="relative shrink-0">
                  <button
                    type="button"
                    onClick={() => setTableDropdownOpen((open) => !open)}
                    className="h-10 px-4 bg-white rounded-[8px] shadow-sm border border-[#E2E8F0] font-sansation font-bold text-[#1F2937] text-[15px] flex items-center gap-2"
                  >
                    Table {cart.tableNo || availableTables[0]}
                    <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
                      <path d="M1 1l4 4 4-4" stroke="#1F2937" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                  {tableDropdownOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setTableDropdownOpen(false)} />
                      <div className="absolute right-0 mt-2 w-[170px] bg-white rounded-[8px] border border-[#E2E8F0] shadow-lg max-h-[260px] overflow-hidden z-50">
                        <div className="p-2 border-b border-[#E8E9F1]">
                          <input
                            type="text"
                            value={tableSearch}
                            onChange={(e) => setTableSearch(e.target.value)}
                            placeholder="Search table"
                            className="w-full h-9 rounded-[6px] border border-[#E2E8F0] px-3 font-opensans text-[13px] outline-none focus:border-[#F15A29]"
                          />
                        </div>
                        <div className="max-h-[210px] overflow-y-auto py-1">
                          {filteredTables.length === 0 ? (
                            <p className="px-5 py-3 font-opensans text-[13px] text-[#8F9098]">
                              No table found
                            </p>
                          ) : (
                            filteredTables.map((tableNo) => (
                              <button
                                key={tableNo}
                                type="button"
                                onClick={() => handleSelectTable(tableNo)}
                                className={`w-full text-left px-5 py-3 font-sansation font-bold text-[14px] border-0 bg-white hover:bg-[#F15A29]/10 hover:text-[#F15A29] ${String(cart.tableNo) === String(tableNo) ? "text-[#F15A29]" : "text-[#475569]"
                                  }`}
                              >
                                Table {tableNo}
                              </button>
                            ))
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
            {/* Line items */}
            {isEmpty ? (
              <div className="px-4 py-12 text-center font-opensans text-[14px] text-[#8F9098]">
                Your cart is empty.
              </div>
            ) : (
              <div className="border-t border-[#E8E9F1] bg-white">
                {cart.lines.map((line, idx) => (
                  <div key={line.key} className={idx !== cart.lines.length - 1 ? "border-b border-[#E8E9F1]" : ""}>
                    <CartLineItem
                      line={line}
                      onIncrement={() => cart.increment(line.key)}
                      onDecrement={() => cart.decrement(line.key)}
                    />
                  </div>
                ))}
              </div>
            )}
            {/* Add more items */}
            <div className="px-3 pt-2 pb-2 flex justify-end">
              <button type="button" onClick={handleAddMoreItems} className="font-sansation font-bold text-[16px] bg-transparent border-0" style={{ color: "#F15A29" }}>
                + Add More Items
              </button>
            </div>
            {/* Billing */}
            {!isEmpty && (
              <div className="mt-4">
                <BillingDetails subtotal={cart.subtotal} tax={cart.tax} total={cart.total} />
              </div>
            )}
          </div>

          {/* Place Order button for Mobile */}
          <div className="block md:hidden fixed bottom-0 left-1/2 -translate-x-1/2 w-full bg-white px-3 py-4 z-50 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
            <button
              type="button"
              disabled={isEmpty || loading}
              onClick={handlePlaceOrder}
              className="w-full rounded-[6px] py-3.5 font-sansation font-bold text-[14px] tracking-[-0.28px] uppercase text-white border-0 disabled:opacity-60 flex items-center justify-center gap-2"
              style={{ backgroundColor: "#F15A29" }}
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Placing Order...
                </>
              ) : (
                "Place Order"
              )}
            </button>
          </div>

          {/* Tablet view container (visible on md screens and above) */}
          <div className="hidden md:block max-w-[1200px] mx-auto px-4 py-6 font-inter">
            {/* Header: Title + top buttons */}
            <div className="flex items-center justify-between mb-6">
              <h1 className="font-sansation font-bold text-[#111] text-[24px]">
                Your Cart {cart.tableNo ? `(Table ${cart.tableNo})` : ""}
              </h1>
              <div className="flex items-center gap-3">
                {availableTables.length > 0 && (
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setTableDropdownOpen((open) => !open)}
                      className="h-[52px] min-w-[160px] px-5 bg-white rounded-[8px] shadow-sm border border-[#E2E8F0] font-sansation font-bold text-[#1F2937] text-[18px] flex items-center justify-center gap-3"
                    >
                      Table {cart.tableNo || availableTables[0]}
                      <svg width="12" height="8" viewBox="0 0 10 6" fill="none">
                        <path d="M1 1l4 4 4-4" stroke="#1F2937" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                    {tableDropdownOpen && (
                      <>
                        <div className="fixed inset-0 z-40" onClick={() => setTableDropdownOpen(false)} />
                        <div className="absolute left-0 mt-2 w-[210px] bg-white rounded-[8px] border border-[#E2E8F0] shadow-lg max-h-[330px] overflow-hidden z-50">
                          <div className="p-2 border-b border-[#E8E9F1]">
                            <input
                              type="text"
                              value={tableSearch}
                              onChange={(e) => setTableSearch(e.target.value)}
                              placeholder="Search table"
                              className="w-full h-9 rounded-[6px] border border-[#E2E8F0] px-3 font-opensans text-[13px] outline-none focus:border-[#F15A29]"
                            />
                          </div>
                          <div className="max-h-[275px] overflow-y-auto py-1">
                            {filteredTables.length === 0 ? (
                              <p className="px-5 py-3 font-opensans text-[13px] text-[#8F9098]">
                                No table found
                              </p>
                            ) : (
                              filteredTables.map((tableNo) => (
                                <button
                                  key={tableNo}
                                  type="button"
                                  onClick={() => handleSelectTable(tableNo)}
                                  className={`w-full text-left px-5 py-3 font-sansation font-bold text-[15px] border-0 bg-white hover:bg-[#F15A29]/10 hover:text-[#F15A29] ${String(cart.tableNo) === String(tableNo) ? "text-[#F15A29]" : "text-[#475569]"
                                    }`}
                                >
                                  Table {tableNo}
                                </button>
                              ))
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
                <button
                  type="button"
                  onClick={handleClearCart}
                  className="font-sansation font-bold text-[14px] px-5 py-2 border-2 border-[#F15A29] rounded-[8px] bg-white text-[#F15A29] uppercase hover:bg-[#FFF5F2] transition-colors"
                >
                  Clear Cart
                </button>
                {/* <button
                  type="button"
                  disabled
                  className="font-sansation font-bold text-[14px] px-5 py-2 border-2 border-gray-300 rounded-[8px] bg-gray-50 text-gray-400 uppercase cursor-not-allowed"
                >
                  Split Cart
                </button> */}
              </div>
            </div>

            {isEmpty ? (
              <div className="bg-white rounded-[12px] border border-[#E6EEFF] p-12 text-center font-opensans text-[16px] text-[#8F9098]">
                Your cart is empty.
              </div>
            ) : (
              <div className="flex flex-col gap-6">
                {/* Flex layout on all tablet/iPad screens (md and up) to prevent layout break */}
                <div className="flex flex-col md:flex-row gap-6 items-start">
                  {/* Left side: Cart Items List */}
                  <div className="w-full md:w-[75%] lg:w-[78%] flex flex-col gap-4">
                    <div className="bg-white rounded-[12px] border border-[#E6EEFF] p-3 lg:p-4 flex flex-col gap-4">
                      {cart.lines.map((line, idx) => {
                        const imageSrc = getDishImage(line.dish.name);
                        const unitPrice = getDishCartPrice(line.dish);
                        const itemTotal = unitPrice * line.quantity;
                        return (
                          <div key={line.key}>
                            <div className="flex items-center justify-between gap-2 lg:gap-4 py-2">
                              {moveMode && (
                                <label className="shrink-0 w-5 h-5 inline-flex items-center justify-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={selectedMoveKeys.includes(line.key)}
                                    onChange={() => toggleMoveSelection(line.key)}
                                    className="w-4 h-4 accent-[#F15A29] cursor-pointer"
                                    aria-label={`Select ${line.dish.name}`}
                                  />
                                </label>
                              )}

                              {/* Dish image */}
                              <div className="w-[60px] h-[60px] lg:w-[72px] lg:h-[72px] rounded-[8px] overflow-hidden bg-gray-100 shrink-0 border border-[#E6EEFF]">
                                <img
                                  src={imageSrc}
                                  alt={line.dish.name}
                                  className="w-full h-full object-cover"
                                />
                              </div>

                              {/* Dish details */}
                              <div className="flex-1 min-w-0 ml-2 lg:ml-3">
                                <div className="flex items-center gap-1.5 lg:gap-2">
                                  <span className="font-sansation font-bold text-[#111] text-[14px] lg:text-[16px] leading-tight whitespace-nowrap">
                                    {line.dish.name}
                                  </span>
                                  <span className="shrink-0">
                                    <VegIcon type={line.dish.type} size={15} />
                                  </span>
                                </div>
                                <p className="font-opensans text-[11px] lg:text-[12px] text-[#8F9098] mt-0.5 lg:mt-1">
                                  Dish Code: {line.dish.code}
                                </p>
                                {line.note && (
                                  <p className="font-opensans text-[11px] lg:text-[12px] text-[#F15A29] font-semibold mt-0.5 lg:mt-1">
                                    {line.note}
                                  </p>
                                )}
                              </div>

                              {/* Single price */}
                              <div className="w-[60px] lg:w-[85px] text-right font-sansation font-bold text-[#111] text-[14px] lg:text-[15px]">
                                ₹{unitPrice.toFixed(2)}
                              </div>

                              {/* Stepper */}
                              <div className="shrink-0 flex items-center justify-between border border-[#F15A29] rounded-[6px] h-[30px] lg:h-[34px] w-[75px] lg:w-[90px] px-1.5 lg:px-2 bg-white">
                                <button type="button" onClick={() => cart.decrement(line.key)} className="text-[#F15A29] font-bold text-[14px] lg:text-[16px] bg-transparent border-0 leading-none"> − </button>
                                <span className="font-sansation font-bold text-[#F15A29] text-[13px] lg:text-[14px]"> {line.quantity} </span>
                                <button type="button" onClick={() => cart.increment(line.key)} className="text-[#F15A29] font-bold text-[14px] lg:text-[16px] bg-transparent border-0 leading-none"> + </button>
                              </div>

                              {/* Total price for item */}
                              <div className="w-[70px] lg:w-[95px] text-right font-sansation font-bold text-[#111] text-[14px] lg:text-[16px]">
                                ₹{itemTotal.toFixed(2)}
                              </div>

                              {/* Delete button */}
                              <button
                                type="button"
                                onClick={() => cart.remove(line.key)}
                                className="text-gray-400 hover:text-red-500 transition-colors p-1 shrink-0"
                                aria-label="Remove item"
                              >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="3 6 5 6 21 6" />
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                </svg>
                              </button>
                            </div>
                            {idx !== cart.lines.length - 1 && (<div className="border-b border-[#E8E9F1] my-2" />)}
                          </div>
                        );
                      })}
                    </div>

                    {/* Add more items button */}
                    <button type="button" onClick={handleAddMoreItems} className="w-full py-3.5 border-2 border-dashed border-[#F15A29] rounded-[10px] text-[#F15A29] font-sansation font-bold text-[16px] flex items-center justify-center gap-2 bg-white hover:bg-[#FFF5F2] transition-colors"> + Add More Items </button>
                  </div>

                  {/* Right side: Billing card */}
                  <div className="w-full md:w-[25%] lg:w-[22%] flex flex-col gap-4">
                    <div className="bg-white rounded-[12px] border border-[#E6EEFF] p-4 flex flex-col gap-4">
                      {/* Billing Breakdown */}
                      <div className="flex flex-col gap-3">
                        <div className="flex justify-between items-center text-[14px] text-[#494A50] font-opensans">
                          <span>Sub Total</span>
                          <span className="font-semibold text-[#1F2024]">₹{cart.subtotal.toFixed(2)}</span>
                        </div>
                        {/* Discount is commented out as requested */}
                        {/* <div className="flex justify-between items-center text-[14px] text-green-600 font-opensans">
                          <span>Discount</span>
                          <span className="font-semibold">- ₹0.00</span>
                        </div> */}
                        <div className="flex justify-between items-center text-[14px] text-[#494A50] font-opensans">
                          <span>CGST (2.5%)</span>
                          <span className="font-semibold text-[#1F2024]">₹{(cart.tax / 2).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center text-[14px] text-[#494A50] font-opensans">
                          <span>SGST (2.5%)</span>
                          <span className="font-semibold text-[#1F2024]">₹{(cart.tax / 2).toFixed(2)}</span>
                        </div>
                      </div>

                      <div className="border-t border-[#E8E9F1]" />

                      {/* Grand Total */}
                      <div className="flex justify-between items-center py-1 gap-2">
                        <span className="font-sansation font-bold text-[#1F2024] text-[15px] lg:text-[18px] whitespace-nowrap">
                          Grand Total
                        </span>
                        <span className="font-sansation font-bold text-[#F15A29] text-[15px] lg:text-[22px] whitespace-nowrap">
                          ₹{cart.total.toFixed(2)}
                        </span>
                      </div>

                      {/* Order Note (Optional) */}
                      <div className="flex flex-col gap-2 mt-2">
                        <label htmlFor="tablet-kitchen-note" className="font-opensans font-bold text-[12px] text-[#111]">
                          Order Note (Optional)
                        </label>
                        <textarea
                          id="tablet-kitchen-note"
                          value={kitchenNote}
                          onChange={(e) => setKitchenNote(e.target.value)}
                          placeholder="Enter note for kitchen..."
                          rows={3}
                          className="w-full resize-none rounded-[8px] border border-[#C5C6CC] bg-white px-4 py-3 font-opensans text-[14px] text-[#111] outline-none placeholder:text-[#8F9098] focus:border-[#F15A29]"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Actions Row - Spans full container width below grid */}
                <div className="grid grid-cols-3 gap-4 mt-2">
                  <button
                    type="button"
                    disabled
                    className="py-3.5 border border-[#E6EEFF] rounded-[8px] bg-white text-gray-500 font-sansation font-bold text-[14px] uppercase flex items-center justify-center gap-1.5 opacity-60 cursor-not-allowed"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                      <polyline points="17 21 17 13 7 13 7 21" />
                      <polyline points="7 3 7 8 15 8" />
                    </svg>
                    Save Draft
                  </button>
                  <button
                    type="button"
                    disabled
                    className="py-3.5 border border-[#E6EEFF] rounded-[8px] bg-white text-gray-500 font-sansation font-bold text-[14px] uppercase flex items-center justify-center gap-1.5 opacity-60 cursor-not-allowed"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="6 9 6 2 18 2 18 9" />
                      <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                      <rect x="6" y="14" width="12" height="8" />
                    </svg>
                    Print KOT
                  </button>
                  <button
                    type="button"
                    disabled={isEmpty || loading}
                    onClick={handlePlaceOrder}
                    className="py-3.5 rounded-[8px] text-white font-sansation font-bold text-[14px] uppercase flex items-center justify-center gap-1.5 transition-colors disabled:opacity-60"
                    style={{ backgroundColor: "#F15A29" }}
                  >
                    {loading ? "Placing..." : "Place Order"}
                    {!loading && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <line x1="5" y1="12" x2="19" y2="12" />
                        <polyline points="12 5 19 12 12 19" />
                      </svg>
                    )}
                  </button>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <button
                    type="button"
                    onClick={toggleMoveMode}
                    className={`py-3.5 rounded-[8px] font-sansation font-bold text-[14px] uppercase flex items-center justify-center gap-1.5 transition-colors border ${moveMode
                      ? "bg-[#F15A29] text-white border-[#F15A29]"
                      : "bg-white text-[#1F2024] border-[#E6EEFF] hover:border-[#F15A29] hover:text-[#F15A29]"
                      }`}
                  >
                    Move Item
                  </button>

                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setMoveTableDropdownOpen((open) => !open)}
                      className="w-full py-3.5 px-4 rounded-[8px] border border-[#E6EEFF] bg-white text-[#1F2024] font-sansation font-bold text-[14px] uppercase flex items-center justify-center gap-2 hover:border-[#F15A29]"
                    >
                      {moveTargetTable ? `Table ${moveTargetTable}` : "Select Table"}
                      <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
                        <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>

                    {moveTableDropdownOpen && (
                      <>
                        <div className="fixed inset-0 z-40" onClick={() => setMoveTableDropdownOpen(false)} />
                        <div className="absolute left-0 bottom-[calc(100%+8px)] w-full min-w-[210px] bg-white rounded-[8px] border border-[#E2E8F0] shadow-lg max-h-[300px] overflow-hidden z-50">
                          <div className="p-2 border-b border-[#E8E9F1]">
                            <input
                              type="text"
                              value={moveTableSearch}
                              onChange={(e) => setMoveTableSearch(e.target.value)}
                              placeholder="Search table"
                              className="w-full h-9 rounded-[6px] border border-[#E2E8F0] px-3 font-opensans text-[13px] outline-none focus:border-[#F15A29]"
                            />
                          </div>
                          <div className="max-h-[240px] overflow-y-auto py-1">
                            {filteredMoveTables.length === 0 ? (
                              <p className="px-5 py-3 font-opensans text-[13px] text-[#8F9098]">
                                No table found
                              </p>
                            ) : (
                              filteredMoveTables.map((tableNo) => (
                                <button
                                  key={tableNo}
                                  type="button"
                                  onClick={() => {
                                    setMoveTargetTable(tableNo);
                                    setMoveTableDropdownOpen(false);
                                    setMoveTableSearch("");
                                  }}
                                  className={`w-full text-left px-5 py-3 font-sansation font-bold text-[15px] border-0 bg-white hover:bg-[#F15A29]/10 hover:text-[#F15A29] ${String(moveTargetTable) === String(tableNo) ? "text-[#F15A29]" : "text-[#475569]"
                                    }`}
                                >
                                  Table {tableNo}
                                </button>
                              ))
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={handleMoveItems}
                    disabled={!moveMode || selectedMoveKeys.length === 0 || !moveTargetTable}
                    className="py-3.5 rounded-[8px] text-white font-sansation font-bold text-[14px] uppercase flex items-center justify-center gap-1.5 transition-colors disabled:opacity-60"
                    style={{ backgroundColor: "#F15A29" }}
                  >
                    Move
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Cart;
